import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import {
	Box,
	Button,
	Flex,
	Input,
	InputGroup,
	InputRightElement,
	Spinner,
	Tag,
	TagCloseButton,
	TagLabel,
	Text,
	useToast,
	VStack,
	Wrap,
	WrapItem,
} from '@bonusly/design-system';

import { useDebounceValue } from '../../hooks/use-debounce-value';
import { useGithubSearchRepos, useGithubUpdatePreferences } from '../../queries/github';
import { GithubRepo } from '../../schemas/github';

interface GithubScopePickerProps {
	selectedOrgs: string[];
	selectedRepos: string[];
}

export const GithubScopePicker: FC<GithubScopePickerProps> = ({ selectedOrgs: initialOrgs, selectedRepos: initialRepos }) => {
	const [selectedOrgs, setSelectedOrgs] = useState<string[]>(initialOrgs);
	const [selectedRepos, setSelectedRepos] = useState<string[]>(initialRepos);
	const [searchQuery, setSearchQuery] = useState('');
	const debouncedQuery = useDebounceValue(searchQuery, 300);
	const toast = useToast();

	// Sync local state when props change (e.g. after save + refetch)
	useEffect(() => {
		setSelectedOrgs(initialOrgs);
	}, [initialOrgs]);

	useEffect(() => {
		setSelectedRepos(initialRepos);
	}, [initialRepos]);

	const { data: searchResults, isLoading: isSearching } = useGithubSearchRepos(debouncedQuery);
	const { mutateAsync: updatePreferences, isLoading: isSaving } = useGithubUpdatePreferences();

	const isDirty = useMemo(() => {
		if (initialOrgs.length !== selectedOrgs.length || initialRepos.length !== selectedRepos.length) return true;
		return !initialOrgs.every((o) => selectedOrgs.includes(o)) || !initialRepos.every((r) => selectedRepos.includes(r));
	}, [initialOrgs, initialRepos, selectedOrgs, selectedRepos]);

	const addRepo = useCallback(
		(repo: GithubRepo) => {
			if (!selectedRepos.includes(repo.fullName)) {
				setSelectedRepos((prev) => [...prev, repo.fullName]);
			}
			setSearchQuery('');
		},
		[selectedRepos]
	);

	const addOrg = useCallback(
		(owner: string) => {
			if (!selectedOrgs.includes(owner)) {
				setSelectedOrgs((prev) => [...prev, owner]);
			}
			setSearchQuery('');
		},
		[selectedOrgs]
	);

	const removeRepo = useCallback((fullName: string) => {
		setSelectedRepos((prev) => prev.filter((r) => r !== fullName));
	}, []);

	const removeOrg = useCallback((login: string) => {
		setSelectedOrgs((prev) => prev.filter((o) => o !== login));
	}, []);

	const handleSave = useCallback(async () => {
		try {
			await updatePreferences({
				selected_orgs: selectedOrgs,
				selected_repos: selectedRepos,
			});
		} catch {
			toast({ title: 'Failed to save preferences', status: 'error', duration: 3000 });
		}
	}, [selectedOrgs, selectedRepos, updatePreferences, toast]);

	const hasSelections = selectedOrgs.length > 0 || selectedRepos.length > 0;

	const isSearchActive = debouncedQuery.length >= 2;

	// Only use results when search is active — prevents stale results lingering after clearing input
	// Also filter out repos belonging to an already-selected org
	const filteredResults = useMemo(() => {
		if (!isSearchActive || !searchResults?.repos) return [];
		return searchResults.repos.filter((r) => !selectedRepos.includes(r.fullName) && !selectedOrgs.includes(r.owner));
	}, [isSearchActive, searchResults, selectedRepos, selectedOrgs]);

	// Only offer "add entire org" when the query matches the org name itself
	const resultOrgs = useMemo(() => {
		const q = debouncedQuery.toLowerCase();
		const orgOwners = new Set(filteredResults.filter((r) => r.ownerType === 'Organization').map((r) => r.owner));
		return Array.from(orgOwners).filter((o) => !selectedOrgs.includes(o) && o.toLowerCase().includes(q));
	}, [filteredResults, selectedOrgs, debouncedQuery]);

	return (
		<Box mt={3}>
			<Text fontSize="xs" fontWeight="semibold" color="text.soft" mb={1}>
				Shared with Bizy
			</Text>
			<Text fontSize="xs" color="text.softer" mb={2}>
				{hasSelections ? 'Bizy can only see repos and orgs you select.' : 'Nothing shared yet — Bizy cannot see any repos.'}
			</Text>

			{hasSelections && (
				<Wrap spacing={1} mb={2}>
					{selectedOrgs.map((org) => (
						<WrapItem key={`org:${org}`}>
							<Tag size="sm" colorScheme="blue" borderRadius="full">
								<TagLabel fontSize="xs" wordBreak="break-all">
									org:{org}
								</TagLabel>
								<TagCloseButton onClick={() => removeOrg(org)} />
							</Tag>
						</WrapItem>
					))}
					{selectedRepos.map((repo) => (
						<WrapItem key={`repo:${repo}`}>
							<Tag size="sm" borderRadius="full">
								<TagLabel fontSize="xs" wordBreak="break-all">
									repo:{repo}
								</TagLabel>
								<TagCloseButton onClick={() => removeRepo(repo)} />
							</Tag>
						</WrapItem>
					))}
				</Wrap>
			)}

			<InputGroup size="sm" mb={1}>
				<Input placeholder="Search repos..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
				{isSearching && isSearchActive && (
					<InputRightElement pointerEvents="none">
						<Spinner size="xs" />
					</InputRightElement>
				)}
			</InputGroup>

			{filteredResults.length > 0 && (
				<Box maxH="160px" overflowY="auto" borderWidth="1px" borderColor="border.soft" borderRadius="md" mb={2}>
					<VStack spacing={0} align="stretch">
						{resultOrgs.map((owner) => (
							<Flex
								key={`org-add:${owner}`}
								px={3}
								py={1.5}
								cursor="pointer"
								_hover={{ bgColor: 'grayAlpha.100' }}
								onClick={() => addOrg(owner)}
								alignItems="center"
							>
								<Text fontSize="xs" fontWeight="semibold" wordBreak="break-all">
									org:{owner} (entire org)
								</Text>
							</Flex>
						))}
						{filteredResults.map((repo) => (
							<Flex
								key={repo.fullName}
								px={3}
								py={1.5}
								cursor="pointer"
								_hover={{ bgColor: 'grayAlpha.100' }}
								onClick={() => addRepo(repo)}
								alignItems="center"
							>
								<Text fontSize="xs" wordBreak="break-all">
									repo:{repo.fullName}
								</Text>
								{repo.private && (
									<Text fontSize="xs" color="text.softer" ml={1} flexShrink={0}>
										(private)
									</Text>
								)}
							</Flex>
						))}
					</VStack>
				</Box>
			)}

			{isDirty && (
				<Button size="xs" colorScheme="primary" onClick={handleSave} isLoading={isSaving}>
					Save
				</Button>
			)}
		</Box>
	);
};
